import { Component, inject, Input, OnChanges, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import {
  ApiTokenInfo,
  ApiTokenService,
} from 'src/app/shared/services/api-token.service';
import { NotificationService } from 'src/app/core/services/notification.service';

/**
 * "API access" section of the Subscription tab (My Account > Billing):
 * lets a company admin generate, view, and revoke the Bearer token used to
 * call the external API (/api/external/v1/*). Only shown when the
 * company's effective pack has useApi enabled — see BillingComponent.
 */
@Component({
  selector: 'app-api-access',
  imports: [TranslocoPipe, ButtonModule, InputTextModule, DatePipe],
  templateUrl: './api-access.component.html',
})
export class ApiAccessComponent implements OnChanges {
  private apiTokenService = inject(ApiTokenService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);

  @Input({ required: true }) companyId!: string;
  @Input() useApi = false;

  tokenInfo = signal<ApiTokenInfo | null>(null);
  usage = signal<{ used: number; limit: number } | null>(null);
  /** The raw token, shown exactly once right after generation. Never persisted. */
  generatedToken = signal<string | null>(null);
  loading = signal(false);
  confirmingRevoke = signal(false);
  confirmingRegenerate = signal(false);

  ngOnChanges() {
    if (this.useApi && this.companyId) {
      this.load();
    }
  }

  private load() {
    this.loading.set(true);
    this.apiTokenService.getTokenInfo(this.companyId).subscribe({
      next: ({ token }) => {
        this.tokenInfo.set(token);
        this.loading.set(false);
      },
      error: () => {
        this.notificationService.error(
          this.translocoService.translate('apiAccess.error.loadingToken'),
        );
        this.loading.set(false);
      },
    });
    this.apiTokenService.getUsage(this.companyId).subscribe({
      next: (usage) => this.usage.set(usage),
      // Usage is a secondary display detail — a failed fetch here shouldn't
      // block or clutter the token management UI with its own error toast.
      error: () => this.usage.set(null),
    });
  }

  generate() {
    this.confirmingRegenerate.set(false);
    this.loading.set(true);
    this.apiTokenService.generateToken(this.companyId).subscribe({
      next: ({ token }) => {
        this.generatedToken.set(token);
        this.loading.set(false);
        this.load();
      },
      error: () => {
        this.notificationService.error(
          this.translocoService.translate('apiAccess.error.generating'),
        );
        this.loading.set(false);
      },
    });
  }

  confirmRevoke() {
    this.confirmingRevoke.set(true);
  }
  cancelRevoke() {
    this.confirmingRevoke.set(false);
  }
  revoke() {
    this.loading.set(true);
    this.apiTokenService.revokeToken(this.companyId).subscribe({
      next: () => {
        this.tokenInfo.set(null);
        this.confirmingRevoke.set(false);
        this.loading.set(false);
        this.notificationService.success(
          this.translocoService.translate('apiAccess.success.revoked'),
        );
      },
      error: () => {
        this.notificationService.error(
          this.translocoService.translate('apiAccess.error.revoking'),
        );
        this.loading.set(false);
      },
    });
  }

  confirmRegenerate() {
    this.confirmingRegenerate.set(true);
  }
  cancelRegenerate() {
    this.confirmingRegenerate.set(false);
  }

  dismissGeneratedToken() {
    this.generatedToken.set(null);
  }

  async copyToken() {
    const token = this.generatedToken();
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      this.notificationService.success(
        this.translocoService.translate('apiAccess.tokenGenerated.copied'),
      );
    } catch {
      // Clipboard API can be unavailable (permissions, non-secure context) —
      // the token stays visible and selectable in the input either way.
    }
  }
}
